import React from "react";
import Link from "next/link";
import Icon from "./Icon";
import Button from "./Button";
import renderIcon from "../../../components/renderIcon";
import { applyLang } from "../../../i18n";
import { buildDynamicArticlesMenuItems, buildContentHubHomeUrlForSite } from "../../../components/ContentHub/utils";
import { buildMenuUrl, isExternalUrl, buildLinkHref } from "../../../components/normalizeMenuUrl";
import { filterMenuByPlatform } from "../../../components/menuPlatform";
import { getLegacyMenuVisibility } from "./navigation";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";

function DrawerGroup({ icon, label, children, defaultOpen = true }) {
    const [open, setOpen] = React.useState(defaultOpen);
    const hasChildren = React.Children.count(children) > 0;
    return (
        <div className="tr-drawer__group">
            <button
                className="tr-drawer__grouphead tr-focusable"
                aria-expanded={open}
                onClick={() => setOpen(v => !v)}
            >
                {icon && <Icon name={icon} size={18} />}
                <span>{label}</span>
                {hasChildren && (
                    <span className="tr-drawer__chev">
                        <Icon name="chevronRight" size={16} />
                    </span>
                )}
            </button>
            {open && children}
        </div>
    );
}

function DrawerItem({ label, href, onClick, placeholder }) {
    if (placeholder) {
        return (
            <span className="tr-drawer__item tr-drawer__item--placeholder">
                <span>{label}</span>
            </span>
        );
    }
    if (href) {
        return (
            <Link href={buildLinkHref(href)} legacyBehavior>
                <a className="tr-drawer__item tr-focusable">
                    <span>{label}</span>
                </a>
            </Link>
        );
    }
    return (
        <button className="tr-drawer__item tr-focusable" onClick={onClick}>
            <span>{label}</span>
        </button>
    );
}

// Récursif : collapse en cascade pour N niveaux de sous-menus.
// - `#` (ou url vide) = placeholder non-cliquable.
// - item sans enfants = DrawerItem simple.
// - item avec enfants = DrawerGroup qui se replie et contient d'autres DrawerMenuNode.
function DrawerMenuNode({ item, fullMenu, org, lang, depth = 0, contentHubCategories }) {
    const localized = applyLang(item, lang);
    const isDynamicArticles = item.type === "DYNAMIC_ARTICLES";
    const rawUrl = (localized.url || "").trim();

    // Menu dynamique : parent -> home ContentHub ; on injecte tout l'arbre des
    // catégories (récursif) dans le fullMenu pour la cascade imbriquée.
    let effectiveFullMenu = fullMenu;
    if (isDynamicArticles) {
        const site = contentHubCategories?.site;
        effectiveFullMenu = [
            ...fullMenu,
            ...buildDynamicArticlesMenuItems(contentHubCategories?.items, item.id, site, org),
        ];
    }

    const isPlaceholder = isDynamicArticles ? false : (!rawUrl || rawUrl === "#");
    const url = isDynamicArticles
        ? buildContentHubHomeUrlForSite(org, contentHubCategories?.site)
        : (isPlaceholder ? null : buildMenuUrl(rawUrl, org));
    const children = effectiveFullMenu.filter(i => i.parent === item.id);

    // Padding horizontal pour les niveaux imbriqués (pas le niveau 0 qui a déjà son gutter)
    const wrapperStyle = depth > 0
        ? { paddingInlineStart: 12, paddingInlineEnd: 12, paddingBlock: 4 }
        : undefined;

    if (children.length === 0) {
        return (
            <div style={wrapperStyle}>
                <DrawerItem label={localized.name} href={url} placeholder={isPlaceholder} />
            </div>
        );
    }

    // Preserve short menus; larger menus start compact so every root remains
    // discoverable before expanding its children. Donation groups keep their
    // independent default-open behavior below.
    const defaultOpen = fullMenu.length <= 8 && children.length <= 3;

    return (
        <div style={wrapperStyle}>
            <DrawerGroup label={localized.name} defaultOpen={defaultOpen}>
                {/* Si l'item parent a une vraie URL, on affiche un lien "tout voir" en tête.
                    Pour un menu dynamique, ce lien mène à la home ContentHub. */}
                {url && (
                    <DrawerItem label={localized.name} href={url} />
                )}
                {children.map(child => (
                    <DrawerMenuNode
                        key={child.id}
                        item={child}
                        fullMenu={effectiveFullMenu}
                        org={org}
                        lang={lang}
                        depth={depth + 1}
                        contentHubCategories={contentHubCategories}
                    />
                ))}
            </DrawerGroup>
        </div>
    );
}

export default function MobileDrawer({ open, onClose, context, openerRef }) {
    const { org, config, contentHubCategories } = context;
    const panelRef = React.useRef(null);
    const scrimRef = React.useRef(null);
    const closeButtonRef = React.useRef(null);
    const onCloseRef = React.useRef(onClose);
    onCloseRef.current = onClose;

    React.useEffect(() => {
        if (!open) return;
        const panel = panelRef.current;
        if (!panel) return;
        const opener = openerRef?.current || document.activeElement;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        // The drawer is modal. Keep its own branch and clickable scrim active,
        // and restore each background element's original inert state on close.
        const background = [];
        let branch = panel;
        while (branch.parentElement) {
            const parent = branch.parentElement;
            for (const sibling of parent.children) {
                if (sibling === branch || sibling === scrimRef.current ||
                    ["STYLE", "SCRIPT", "LINK", "HEAD"].includes(sibling.tagName)) continue;
                background.push([sibling, sibling.inert]);
                sibling.inert = true;
            }
            if (parent === document.body) break;
            branch = parent;
        }

        const focusable = () => Array.from(panel.querySelectorAll(
            "a[href], button, input, select, textarea, [tabindex], [contenteditable='true']"
        )).filter(element => element.tabIndex >= 0 && element.getClientRects().length > 0 &&
            !element.matches(":disabled, [aria-disabled='true']") &&
            !element.closest("[hidden], [inert], [aria-hidden='true']"));
        const focusStart = () => (closeButtonRef.current || focusable()[0] || panel).focus({ preventScroll: true });
        const onKey = (e) => {
            if (e.defaultPrevented) return;
            if (e.key === "Escape") {
                e.preventDefault();
                onCloseRef.current?.();
            } else if (e.key === "Tab") {
                const targets = focusable();
                const first = targets[0];
                const last = targets[targets.length - 1];
                const current = document.activeElement;
                if (!first) {
                    e.preventDefault();
                    panel.focus({ preventScroll: true });
                } else if (!targets.includes(current) || (e.shiftKey ? current === first : current === last)) {
                    e.preventDefault();
                    (e.shiftKey ? last : first).focus({ preventScroll: true });
                }
            }
        };
        const onFocus = (e) => {
            if (!panel.contains(e.target)) focusStart();
        };
        document.addEventListener("keydown", onKey);
        document.addEventListener("focusin", onFocus);
        focusStart();
        return () => {
            document.body.style.overflow = prev;
            document.removeEventListener("keydown", onKey);
            document.removeEventListener("focusin", onFocus);
            for (const [element, inert] of background) element.inert = inert;
            if (opener?.isConnected && typeof opener.focus === "function") opener.focus({ preventScroll: true });
        };
    }, [open, openerRef]);

    if (!open) return null;

    const menu = filterMenuByPlatform(org.web_menu || [], context);
    menu.sort((a, b) => a.position - b.position);
    const localizedNavigation = menu.map(item => applyLang(item, context.lang));
    const legacyMenu = getLegacyMenuVisibility(localizedNavigation, org, config);

    const topMenu = menu.filter(i => !i.parent);

    // Boutons configurables (max 2) — pattern identique à Travel,
    // stockés sous config.menu (édités via /web/theme onglet "Menu").
    const localizedMenu = applyLang(config?.menu || {}, context.lang);
    const menuButtons = [1, 2]
        .map((n) => ({
            label: localizedMenu[`button_${n}_label`],
            url: localizedMenu[`button_${n}_url`],
            icon: localizedMenu[`button_${n}_icon`],
        }))
        .filter((b) => b.label && b.url);

    const caisses = (org.caisses || [])
        .filter(c => (c.payed < c.objectif || !c.withObjectif) && c.active && !c.paused && !c.hidden)
        .map(i => applyLang(i, context.lang));

    // Ferme le drawer quand on clique sur n'importe quel lien <a> à l'intérieur.
    // Pratique : ça couvre tous les liens (menu auto, custom, social, CTA footer)
    // sans avoir à passer onClose manuellement dans chaque sous-composant.
    const handleContentClick = (e) => {
        if (e.target.closest("a")) {
            onClose?.();
        }
    };

    return (
        <>
            <div ref={scrimRef} className="tr-drawer-scrim" onClick={onClose} />
            <aside
                ref={panelRef}
                className="tr-drawer"
                role="dialog"
                aria-modal="true"
                aria-label={t`Menu`}
                tabIndex={-1}
                onClick={handleContentClick}
            >
                <div className="tr-drawer__head">
                    {org.logo && (
                        <img src={org.logo} alt={org.name} />
                    )}
                    <button
                        ref={closeButtonRef}
                        className="tr-drawer__close tr-focusable"
                        onClick={onClose}
                        aria-label={t`Fermer`}
                    >
                        <Icon name="close" size={18} />
                    </button>
                </div>

                <div className="tr-drawer__body">
                    {/* Menu principal — rendu récursif en cascade (collapses imbriqués) */}
                    {!org.limited && topMenu.map(item => (
                        <DrawerMenuNode
                            key={item.id}
                            item={item}
                            fullMenu={menu}
                            org={org}
                            lang={context.lang}
                            contentHubCategories={contentHubCategories}
                        />
                    ))}

                    {/* Services communautaires : items au premier plan (pas de groupe). */}
                    {legacyMenu.horaire && (
                        <DrawerItem label={t`Horaires`} href={org.BASE_WEB_URL + "/horaires"} />
                    )}
                    {legacyMenu.event && (
                        <DrawerItem label={t`Événements`} href={org.BASE_WEB_URL + "/evenements"} />
                    )}
                    {legacyMenu.galerie && (
                        <DrawerItem label={t`Galeries`} href={org.BASE_WEB_URL + "/galeries"} />
                    )}
                    {legacyMenu.abo && (
                        <DrawerItem label={t`Abonnements`} href={org.BASE_WEB_URL + "/abonnements"} />
                    )}

                    {/* Don : groupe replié si > 3 sous-items (Don Spontané + caisses). */}
                    {config["menu.don.active"] !== false && caisses.length > 0 && (
                        <DrawerGroup icon="heart" label={t`Faire un don`} defaultOpen={1 + caisses.length <= 3}>
                            <DrawerItem label={t`Don Spontané`} href={org.BASE_WEB_URL + "/don"} />
                            {caisses.map(c => (
                                <DrawerItem key={c.id} label={c.name} href={org.BASE_WEB_URL + "/don/" + c.id} />
                            ))}
                        </DrawerGroup>
                    )}
                    {config["menu.don.active"] !== false && caisses.length === 0 && (
                        <DrawerItem label={t`Faire un don`} href={org.BASE_WEB_URL + "/don"} />
                    )}

                    {/* Réseaux sociaux */}
                    <div className="tr-drawer__socials">
                        {config["social.fb"] && (
                            <a href={config["social.fb"]} target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                                <Icon name="facebook" size={18} /> Facebook
                            </a>
                        )}
                        {config["social.insta"] && (
                            <a href={config["social.insta"]} target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                                <Icon name="instagram" size={18} /> Instagram
                            </a>
                        )}
                        {config["social.whatsapp"] && (
                            <a href={config["social.whatsapp"]} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp">
                                <Icon name="whatsapp" size={18} /> WhatsApp
                            </a>
                        )}
                        {config["social.yt"] && (
                            <a href={config["social.yt"]} target="_blank" rel="noopener noreferrer" aria-label="YouTube">
                                {renderIcon({ name: "IconBrandYoutube", source: "tabler" }, 18)} YouTube
                            </a>
                        )}
                        {config["social.tiktok"] && (
                            <a href={config["social.tiktok"]} target="_blank" rel="noopener noreferrer" aria-label="TikTok">
                                {renderIcon({ name: "IconBrandTiktok", source: "tabler" }, 18)} TikTok
                            </a>
                        )}
                    </div>
                </div>

                <div className="tr-drawer__foot">
                    {menuButtons.length > 0 && menuButtons.slice(0, 2).map((btn, i) => {
                        const url = buildMenuUrl(btn.url, org);
                        // Externe = absolu qui n'a pas été réécrit vers le domaine custom.
                        const isExternal = isExternalUrl(url, org);
                        const buttonEl = (
                            <Button variant={i === 0 ? "primary" : "secondary"} size="sm" as="a" style={{ flex: 1 }}>
                                {btn.icon && renderIcon(btn.icon, 14)}
                                <span className="tr-btn-label">{btn.label}</span>
                            </Button>
                        );
                        if (isExternal) {
                            return (
                                <a key={i} href={url} style={{ textDecoration: "none", flex: 1, display: "flex" }}>
                                    {buttonEl}
                                </a>
                            );
                        }
                        return (
                            <Link key={i} href={url} legacyBehavior>
                                {buttonEl}
                            </Link>
                        );
                    })}

                    {menuButtons.length === 0 && config["menu.don.active"] !== false && (
                        <Link href={buildLinkHref(org.BASE_WEB_URL + "/don", org)} legacyBehavior>
                            <Button variant="primary" size="sm" as="a" style={{ flex: 1 }}>
                                <Icon name="heart" size={14} />
                                <Trans>Don</Trans>
                            </Button>
                        </Link>
                    )}
                    {menuButtons.length === 0 && config["menu.horaire.active"] !== false && (
                        <Link href={buildLinkHref(org.BASE_WEB_URL + "/horaires", org)} legacyBehavior>
                            <Button variant="secondary" size="sm" as="a" style={{ flex: 1 }}>
                                <Icon name="candles" size={14} />
                                <Trans>Shabbat</Trans>
                            </Button>
                        </Link>
                    )}
                </div>
            </aside>
        </>
    );
}
